import { FC, useContext, useState } from 'react'
import { ChatbotUIContext } from '@/context/context'
import { PROMPT_NAME_MAX } from '@/db/limits'
import { TablesInsert } from '@/supabase/types'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TextareaAutosize } from '@/components/ui/textarea-autosize'
import { SidebarCreateItem } from '@/components/sidebar/items/all/sidebar-create-item'
import { AIGenerationInput } from '@/components/ui/ai-generation-input'
import { SharingField } from '@/components/sidebar/items/all/sharing-field'

interface CreatePromptProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

interface PromptGenerationResponse {
  name: string
  content: string
}

export const CreatePrompt: FC<CreatePromptProps> = ({ isOpen, onOpenChange }) => {
  const { profile, selectedWorkspace } = useContext(ChatbotUIContext)
  const { t } = useTranslation()
  const [isTyping, setIsTyping] = useState(false)
  const [name, setName] = useState('')
  const [content, setContent] = useState('')
  const [sharing, setSharing] = useState('private')
  const [isGenerating, setIsGenerating] = useState(false)
  const [showAIInput, setShowAIInput] = useState(true)
  const [userPrompt, setUserPrompt] = useState('')
  const [hasGenerated, setHasGenerated] = useState(false)

  const handlePromptGeneration = async (description: string) => {
    try {
      setIsGenerating(true)
      setUserPrompt(description)
      const response = await authenticatedFetch('/api/chat/marshmallow/generate-prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userInput: description
        })
      })

      if (!response.ok) throw new Error('Failed to generate prompt')

      const data: PromptGenerationResponse = await response.json()

      setName(data.name)
      setContent(data.content)
      setHasGenerated(true)
      setShowAIInput(false)
    } catch (error) {
      toast.error('Failed to generate prompt')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleRegenerate = async () => {
    if (userPrompt) {
      await handlePromptGeneration(userPrompt)
    }
  }

  const handleEditPrompt = () => {
    setShowAIInput(true)
  }

  const handleManualCreate = () => {
    setShowAIInput(false)
    setHasGenerated(true)
  }

  if (!profile) return null
  if (!selectedWorkspace) return null

  return (
    <SidebarCreateItem
      contentType="prompts"
      isOpen={isOpen}
      isTyping={isTyping}
      onOpenChange={onOpenChange}
      headerAction={
        hasGenerated &&
        !isGenerating && (
          <div className="flex gap-2">
            <button
              onClick={handleEditPrompt}
              className="hover:bg-accent rounded-md px-3 py-1.5 text-sm transition-colors"
              title={t('Edit prompt')}
            >
              {t('Edit')}
            </button>
            <button
              onClick={handleRegenerate}
              className="hover:bg-accent rounded-md px-3 py-1.5 text-sm transition-colors"
              title={t('Regenerate')}
            >
              {t('Regenerate')}
            </button>
          </div>
        )
      }
      createState={
        {
          user_id: profile.user_id,
          name,
          sharing,
          content
        } as TablesInsert<'prompts'>
      }
      renderInputs={() => (
        <>
          {showAIInput && !hasGenerated && (
            <AIGenerationInput
              onGenerate={handlePromptGeneration}
              isGenerating={isGenerating}
              placeholder={t('Describe what kind of prompt template you want to create...')}
              onSkip={handleManualCreate}
            />
          )}

          {showAIInput && hasGenerated && (
            <div className="mb-4 space-y-2">
              <Label>{t('Your prompt')}</Label>
              <TextareaAutosize
                className="border-input bg-background w-full rounded-md border px-3 py-2"
                value={userPrompt}
                onValueChange={setUserPrompt}
                minRows={2}
                maxRows={4}
              />
              <button
                onClick={() => handlePromptGeneration(userPrompt)}
                disabled={isGenerating || !userPrompt.trim()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isGenerating ? t('Regenerating...') : t('Regenerate')}
              </button>
            </div>
          )}

          {(!showAIInput || hasGenerated) && (
            <>
              <div className="space-y-1">
                <Label>{t('Name')}</Label>

                <Input
                  placeholder={t('Prompt name...')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={PROMPT_NAME_MAX}
                  onCompositionStart={() => setIsTyping(true)}
                  onCompositionEnd={() => setIsTyping(false)}
                />
              </div>

              <div className="space-y-1">
                <Label>{t('Prompt')}</Label>

                <TextareaAutosize
                  placeholder={t('Prompt content...')}
                  value={content}
                  onValueChange={setContent}
                  minRows={6}
                  maxRows={20}
                  onCompositionStart={() => setIsTyping(true)}
                  onCompositionEnd={() => setIsTyping(false)}
                />
              </div>

              <SharingField value={sharing} onChange={setSharing} />
            </>
          )}
        </>
      )}
    />
  )
}
